import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { API_ENDPOINTS } from "../../../config/api";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = API_ENDPOINTS.APP_USERS.BASE;

const AppUserDetailsContent = () => {
  const { id } = useParams();
  const [appUser, setAppUser] = useState(null);
  const [regions, setRegions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppUser();
    fetchRegions();
  }, []);

  const fetchAppUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/${id}`);
      setAppUser(response.data.appUser);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching app user details:", error);
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.REGION_NAMES);
      setRegions(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Regions details:", error);
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const userContent = document.querySelector('.user-content').innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>App User Details</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 15px; 
              line-height: 1.2;
              font-size: 12px;
            }
            .user-content { 
              max-width: 800px; 
              margin: 0 auto; 
            }
            .section-title { 
              font-size: 16px; 
              font-weight: bold; 
              margin: 10px 0 5px 0; 
              color: #333;
            }
            .fields-block { 
              display: flex; 
              align-items: center; 
              margin-bottom: 4px; 
            }
            .fields-block p:first-child { 
              width: 130px; 
              color: #666; 
              font-weight: medium; 
            }
            .fields-block p:last-child { 
              flex: 1; 
              color: #111; 
            }
            .chip { 
              display: inline-flex; 
              padding: 3px 6px; 
              margin: 2px; 
              background-color: #1976d2 !important; 
              border-radius: 6px; 
              font-size: 11px; 
            }
            hr {
              margin: 8px 0;
              border: 0;
              border-top: 1px solid #ddd;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="user-content">
            ${userContent}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = async () => {
    let originalText = 'Download';
    let downloadButton = null;

    try {
      downloadButton = document.querySelector('button[onclick*="handleDownload"]');
      if (downloadButton) {
        originalText = downloadButton.textContent || 'Download';
        downloadButton.textContent = 'Generating PDF...';
        downloadButton.disabled = true;
      }

      const userContent = document.querySelector('.user-content');
      if (!userContent) {
        throw new Error('User content not found');
      }

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '15px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.2';

      const clonedContent = userContent.cloneNode(true);

      const forceStyles = document.createElement('style');
      forceStyles.textContent = `
        * { color: #111 !important; }
        .section-title { font-size: 16px; font-weight: bold; margin: 10px 0 5px 0; color: #333 !important; }
        .fields-block { display: flex; align-items: center; margin-bottom: 4px; }
        .fields-block p:first-child { width: 130px; color: #666 !important; font-weight: medium; }
        .fields-block p:last-child { flex: 1; color: #111 !important; }
        .chip { 
          display: inline-flex !important; 
          padding: 3px 6px !important; 
          margin: 2px !important; 
          background-color: #1976d2 !important; 
          color: white !important; 
          border-radius: 6px !important; 
          font-size: 11px !important; 
        }
        hr {
          margin: 8px 0;
          border: 0;
          border-top: 1px solid #ddd;
        }
      `;
      tempContainer.appendChild(forceStyles);
      tempContainer.appendChild(clonedContent);

      document.body.appendChild(tempContainer);

      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 800,
          height: tempContainer.scrollHeight,
          logging: false,
          removeContainer: false
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');

        const imgWidth = 190;
        const pageHeight = 277;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, position + 10, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        pdf.save(`app-user-${appUser.company_name || 'details'}.pdf`);

      } catch (pdfError) {
        throw new Error('PDF generation failed: ' + pdfError.message);
      } finally {
        document.body.removeChild(tempContainer);
      }

    } catch (error) {
      try {
        const userContent = document.querySelector('.user-content').innerHTML;
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>App User Details</title>
              <meta charset="utf-8">
              <style>
                body { font-family: Arial, sans-serif; margin: 15px; line-height: 1.2; font-size: 12px; }
                .user-content { max-width: 800px; margin: 0 auto; }
                .section-title { font-size: 16px; font-weight: bold; margin: 10px 0 5px 0; color: #333; }
                .fields-block { display: flex; align-items: center; margin-bottom: 4px; }
                .fields-block p:first-child { width: 130px; color: #666; font-weight: medium; }
                .fields-block p:last-child { flex: 1; color: #111; }
                .chip { 
                  display: inline-flex; 
                  padding: 3px 6px; 
                  margin: 2px; 
                  background-color: #1976d2; 
                  color: white; 
                  border-radius: 6px; 
                  font-size: 11px; 
                }
                hr {
                  margin: 8px 0;
                  border: 0;
                  border-top: 1px solid #ddd;
                }
              </style>
            </head>
            <body>
              <div class="user-content">
                ${userContent}
              </div>
            </body>
          </html>
        `;

        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `app-user-${appUser.company_name || 'details'}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        alert('PDF generation failed. Downloaded as HTML instead. You can convert it to PDF using your browser\'s print function (Ctrl+P → Save as PDF).');
      } catch (fallbackError) {
        alert('Error generating PDF. Please try again or contact support.');
      }
    } finally {
      if (downloadButton) {
        downloadButton.textContent = originalText;
        downloadButton.disabled = false;
      }
    }
  };

  if (loading) {
    return (
      <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />
    );
  }

  if (!appUser) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 5 }}>
        App user not found
      </Typography>
    );
  }

  return (
    <Box component="section">
      <Grid container spacing={2}>
        {/* Profile Top Card - Excluded from Print/PDF */}
        <Grid item xs={12}>
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: "12px",
              p: { xs: 2, md: 2 },
              mb: 1,
            }}
          >
            <Box
              className="profile-top"
              sx={{
                display: "flex",
                alignItems: "start",
                gap: 3,
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "start", gap: 3 }}>
                {appUser.profile_pic ? (
                  <img
                    src={appUser.profile_pic}
                    alt={appUser.company_name}
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <AccountCircle sx={{ width: 100, height: 100, color: "#bdbdbd" }} />
                )}
                <div className="profile-top-content">
                  <Typography variant="h4">{appUser.company_name || ""}</Typography>
                  <Typography variant="p">{appUser.user_type?.name || ""}</Typography>
                </div>
              </Box>
              <Link to="/app-users" className="back-button">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ArrowBackIcon />}
                >
                  Back to App Users
                </Button>
              </Link>
            </Box>
          </Card>
        </Grid>
        {/* Main Content for Print/PDF */}
        <Grid item xs={12} className="user-content">
          <Grid container spacing={2} className="equal-height-grid">
            <Grid item xs={12} md={7}>
              {/* Basic Information Card */}
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card
                  className="info-view-card"
                  sx={{
                    boxShadow: 2,
                    borderRadius: "12px",
                    p: { xs: 2, md: 2 },
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" className="section-title">Basic Information</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Contact Person:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.contact_person || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Email:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.email || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Mobile:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.mobile || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Alternate Number:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.alternate_number || ""}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              {/* Tax Information Card */}
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card
                  className="info-view-card"
                  sx={{
                    boxShadow: 2,
                    borderRadius: "12px",
                    p: { xs: 2, md: 2 },
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" className="section-title">Tax Information</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        PAN Number:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.pan || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        GSTIN:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.gstin || ""}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              {/* Address Information Card */}
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card
                  className="info-view-card"
                  sx={{
                    boxShadow: 2,
                    borderRadius: "12px",
                    p: { xs: 2, md: 2 },
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" className="section-title">Address Information</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "start", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Street Line:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.street_line || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "start", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Town/City:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.town_city || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "start", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        State:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.state || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "start", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Country:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.country || ""}</Typography>
                    </Box>
                    <Box className="fields-block" sx={{ display: "flex", justifyContent: "start", alignItems: "start", mb: 0.5 }}>
                      <Typography component="p" variant="p" sx={{ color: "text.secondary", fontWeight: "medium", marginRight: "8px", width: "130px" }}>
                        Pincode:
                      </Typography>
                      <Typography component="p" variant="p">{appUser.pincode || ""}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              {/* Region Card */}
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card
                  className="info-view-card"
                  sx={{
                    boxShadow: 2,
                    borderRadius: "12px",
                    p: { xs: 2, md: 2 },
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" className="section-title">Region</Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box>
                    <Box className="fields-block" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, justifyContent: "start" }}>
                      {regions && Array.isArray(regions) && appUser?.region_id
                        ? regions
                            .filter((region) => {
                              const regionIds = Array.isArray(appUser.region_id)
                                ? appUser.region_id
                                : String(appUser.region_id)
                                    .split(",")
                                    .map((id) => id.trim());
                              return regionIds.includes(region.id.toString());
                            })
                            .map((region) => (
                              <Chip
                                key={region.id}
                                label={region.name}
                                className="chip"
                                color="primary"
                                variant="filled"
                                sx={{ borderRadius: "6px", fontSize: "11px", padding: "2px 4px", height: "auto", lineHeight: "normal" }}
                              />
                            ))
                        : <Typography component="p" variant="p">No regions assigned</Typography>}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* Print and Download Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': { 
                borderColor: '#1565c0',
                bgcolor: 'rgba(25, 118, 210, 0.04)'
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Download
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

export default AppUserDetailsContent;